import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import api from "../../api/client";
import ProductCard from "../../components/ProductCard/ProductCard";
import LoadingScreen from "../../components/ui/Loading/Loading";
import Breadcrumb from "../../components/ui/Breadcrumb/Breadcrumb";
import Pagination from "../../components/ui/Pagination/Pagination";
import {
  Tag,
  ShoppingBag,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";
import "./Offers.css";

const PRODUCTS_PER_PAGE = 9;

function getProductCurrentPrice(product) {
  return Number(product?.final_price ?? product?.sale_price ?? product?.price ?? 0);
}

function getProductOriginalPrice(product) {
  return Number(product?.original_price ?? product?.old_price ?? product?.price ?? 0);
}

function hasProductDiscount(product) {
  const original = getProductOriginalPrice(product);
  const current = getProductCurrentPrice(product);
  return (
    product?.has_discount === true ||
    (original > 0 && current > 0 && current < original)
  );
}

function getDiscountPercent(product) {
  const original = getProductOriginalPrice(product);
  const current = getProductCurrentPrice(product);
  if (original <= 0 || current <= 0 || current >= original) return 0;
  return Math.round(((original - current) / original) * 100);
}

export default function Offers() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [visibleCount, setVisibleCount] = useState(PRODUCTS_PER_PAGE);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setMsg("");
        const res = await api.apiGet("products");
        if (cancelled) return;
        const list = Array.isArray(res?.products) ? res.products : [];
        setProducts(list);
      } catch (e) {
        setMsg(isAr ? "فشل تحميل العروض" : "Failed to load offers");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [isAr]);

  const discountedProducts = useMemo(() => {
    const discounted = products.filter((p) => hasProductDiscount(p));
    return discounted.sort((a, b) => getDiscountPercent(b) - getDiscountPercent(a));
  }, [products]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(discountedProducts.length / PRODUCTS_PER_PAGE)),
    [discountedProducts]
  );
  const currentPageSafe = Math.min(currentPage, totalPages);

  const displayedProducts = useMemo(() => {
    if (isMobile) {
      return discountedProducts.slice(0, visibleCount);
    }
    const start = (currentPageSafe - 1) * PRODUCTS_PER_PAGE;
    return discountedProducts.slice(start, start + PRODUCTS_PER_PAGE);
  }, [discountedProducts, isMobile, visibleCount, currentPageSafe]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => prev + PRODUCTS_PER_PAGE);
  }, []);

  const breadcrumbItems = useMemo(() => {
    return [
      { label: isAr ? "المتجر" : "Shop", link: "/shop" },
      { label: isAr ? "العروض" : "Offers" },
    ];
  }, [isAr]);

  if (loading) {
    return (
      <div className="offers-page" dir={isAr ? "rtl" : "ltr"}>
        <div className="container py-4">
          <LoadingScreen isAr={isAr} />
        </div>
      </div>
    );
  }

  return (
    <div className="offers-page" dir={isAr ? "rtl" : "ltr"}>
      {/* ── Breadcrumb ── */}
      <Breadcrumb items={breadcrumbItems} />

      {/* ── Header  ── */}
      <div className="offers-header">
        <div className="container">
          <div className="offers-header-content">
            <div className="offers-title-wrapper">
              <div className="offers-icon">
                <Tag size={28} />
              </div>
              <div className="offers-title-group">
                <h1 className="offers-title">
                  {isAr ? "العروض" : "Offers"}
                </h1>
                <p className="offers-subtitle">
                  {isAr
                    ? "احصل على أفضل الخصومات قبل أن تنتهي."
                    : "Get the best discounts before they're gone."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Products Section ── */}
      <div className="container">
        {msg && (
          <div className="offers-alert">
            <p>{msg}</p>
          </div>
        )}

        {discountedProducts.length === 0 ? (
          <div className="offers-empty-state">
            <div className="offers-empty-icon">
              <Tag size={48} strokeWidth={1.5} />
            </div>
            <h3>{isAr ? "لا توجد عروض حالياً" : "No offers at the moment"}</h3>
            <p>
              {isAr
                ? "سيتم إضافة العروض قريباً. تفقد المتجر باستمرار!"
                : "Offers will be added soon. Check back regularly!"}
            </p>
            <Link to="/shop" className="btn-favorites-cta">
              <ShoppingBag size={18} />
              {isAr ? "تصفح المتجر" : "Browse Shop"}
              {isAr ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
            </Link>
          </div>
        ) : (
          <>
            <div className="offers-results-bar">
              <div className="offers-results-inner">
                <div className="offers-results-number">
                  <span className="offers-results-count" key={discountedProducts.length}>
                    {discountedProducts.length}
                  </span>
                  <span className="offers-results-accent" />
                </div>
                <span className="offers-results-label">
                  {isAr
                    ? discountedProducts.length === 1
                      ? "عرض خاص"
                      : "عروض خاصة"
                    : discountedProducts.length === 1
                    ? "Special Offer"
                    : "Special Offers"}
                </span>
              </div>
            </div>

            <div className="products-grid offers-products-grid">
              {displayedProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

            {/* Desktop Pagination */}
            <div className="pagination-desktop">
              <Pagination
                currentPage={currentPageSafe}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>

            {/* Mobile Load More */}
            {isMobile && discountedProducts.length > displayedProducts.length && (
              <div className="load-more-wrapper">
                <button className="load-more-btn" onClick={handleLoadMore}>
                  {isAr ? "تحميل المزيد" : "Load More"}
                  <span className="load-more-count">
                    ({displayedProducts.length} / {discountedProducts.length})
                  </span>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}